import { isFixedProduct } from '@vy/services';
import { INTEGRATION_PROVIDERS, type FixedProduct, type UsageProduct } from '@vy/types';
import { isEmptyString, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from '../../../../../../hooks/use-debounce';
import { DEBOUNCE_DELAY } from '../../../../../../components/design/Input/input-consts';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { VySelect } from '@vy/ui-design-system';

type IntegrationProduct = { id: string; name: string };

type ExternalIntegrationProvider =
  | typeof INTEGRATION_PROVIDERS.quickBooks
  | typeof INTEGRATION_PROVIDERS.xero;

type ProductsQueryHook = (
  search: string | undefined,
  options: { keepPreviousData: boolean },
) => { data?: { products?: IntegrationProduct[] } };

type ExternalIntegrationProductSelectorProps = {
  provider: ExternalIntegrationProvider;
  label: string;
  placeholder: string;
  helperText?: string;
  useProductsQuery: ProductsQueryHook;
  'data-vy'?: string;
};

// Only for ERPs using the single `externalIntegration` field; NetSuite/Anrok use `integrationEntities[]`.
export const ExternalIntegrationProductSelector: React.FC<ExternalIntegrationProductSelectorProps> = ({
  provider,
  label,
  placeholder,
  helperText,
  useProductsQuery,
  'data-vy': dataVy,
}) => {
  const { itemIndex: productIndex, item: product } = useChargeContext<FixedProduct | UsageProduct>();
  const { setValue, watch } = useFormContext<PlanInputForm>();

  const productType = isFixedProduct(product) ? 'fixedProducts' : 'usageProducts';
  const externalIntegrationField = `${productType}.${productIndex}.externalIntegration` as const;
  const externalIntegration = watch(externalIntegrationField);

  const isSelectedProvider =
    isNotNullOrUndefined(externalIntegration) && externalIntegration.type === provider;

  const [searchInput, setSearchInput] = useState<string | undefined>(undefined);
  const debouncedSearch = useDebounce(searchInput, DEBOUNCE_DELAY);
  const { data: { products } = {} } = useProductsQuery(debouncedSearch, { keepPreviousData: true });

  const selectedProduct =
    isSelectedProvider && isNotNullOrUndefined(products)
      ? products.find((option) => option.id === externalIntegration.id) ?? null
      : null;

  const handleProductChange = (_event: React.SyntheticEvent, value: IntegrationProduct | null) => {
    if (isNullOrUndefined(value)) {
      setValue(externalIntegrationField, undefined, { shouldDirty: true });
      return;
    }

    setValue(externalIntegrationField, { type: provider, id: value.id }, { shouldDirty: true });
  };

  const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
    if (reason !== 'input') {
      return;
    }
    setSearchInput(isEmptyString(value) ? undefined : value);
  };

  return (
    <VySelect<IntegrationProduct>
      data-vy={dataVy}
      label={label}
      helperText={helperText}
      placeholder={placeholder}
      options={products ?? []}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={selectedProduct}
      onChange={handleProductChange}
      onInputChange={handleInputChange}
    />
  );
};
